import { createFileRoute } from "@tanstack/react-router";
import { getRouterInstance } from "@tanstack/react-start";
import { sitemapStaticPaths, sitemapXML, type SitemapEntry } from "@/lib/sitemap";
import { listPublishedPosts } from "@/lib/blog.functions";
import { listProjectsForSitemap, listPublishedMunicipalities } from "@/lib/projects.functions";
import { getLocaleReadiness } from "@/lib/content.functions";

import { isSitemapRouteIncluded } from "@/lib/sitemap";
import { DEFAULT_LANG } from "@/i18n/config";
import type { AnyRoute, AnyRouter } from "@tanstack/react-router";
import { SITE_URL } from "@/content/site";

/** Taalgebonden routes: $lang wordt vervangen door elke taal die volledig gepubliceerd is. */
function localizedStaticPaths(router: AnyRouter, langs: string[]): string[] {
  const paths = new Set<string>();
  for (const route of Object.values(router.routesById) as AnyRoute[]) {
    const full = route.fullPath;
    if (!isSitemapRouteIncluded(route) || !full.startsWith("/$lang")) continue;
    const rest = full.slice("/$lang".length);
    if (rest.includes("$") || rest.includes("*")) continue;
    for (const lang of langs) {
      paths.add(`/${lang}${rest}`.replace(/\/$/, "") || `/${lang}`);
    }
  }
  return [...paths].sort();
}


export const Route = createFileRoute("/sitemap.xml")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      GET: async () => {
        if (!SITE_URL) {
          return new Response("Sitemap domain not configured", {
            status: 503,
            headers: { "Cache-Control": "no-store" },
          });
        }
        const router = await getRouterInstance();
        let blogPosts: { slug: string; datePublished: string }[] = [];
        let municipalities: { slug: string; lastmod: string }[] = [];
        try {
          [blogPosts, municipalities] = await Promise.all([listPublishedPosts(), listPublishedMunicipalities()]);
        } catch {
          return new Response("Sitemap content unavailable", { status: 503, headers: { "Cache-Control": "no-store" } });
        }
        let projects: { slug: string; lastmod: string }[] = [];
        try {
          projects = await listProjectsForSitemap();
        } catch {
          projects = [];
        }
        // Alleen talen waarvan élke pagina gepubliceerd is, komen in de sitemap.
        let langs: string[] = [DEFAULT_LANG];
        try {
          const readiness = await getLocaleReadiness();
          langs = [DEFAULT_LANG, ...readiness.filter((rij) => rij.volledig).map((rij) => rij.lang)];
        } catch {
          langs = [DEFAULT_LANG];
        }
        const entries: SitemapEntry[] = [
          ...sitemapStaticPaths(router).map((path) => ({ path })),
          ...localizedStaticPaths(router, [...new Set(langs)]).map((path) => ({ path })),

          // Blogartikels lopen via de dynamische route /$lang/blog/$slug en worden hier expliciet toegevoegd.
          ...blogPosts.map((post) => ({
            path: `/${DEFAULT_LANG}/blog/${post.slug}`,
            lastmod: post.datePublished,
          })),
          // Gepubliceerde realisaties lopen via /$lang/realisaties/$slug.
          ...projects.map((project) => ({
            path: `/${DEFAULT_LANG}/realisaties/${project.slug}`,
            lastmod: project.lastmod,
          })),
          ...municipalities.map((municipality) => ({
            path: `/${DEFAULT_LANG}/realisaties/gemeente/${municipality.slug}`,
            lastmod: municipality.lastmod,
          })),
        ];
        if (entries.length === 0) {
          return new Response(
            'No pages are included in this sitemap. Check route decisions and ancestor exclusions. Setting "exclude-subtree" on the root excludes the entire site.',
            { status: 404, headers: { "Cache-Control": "no-store" } },
          );
        }
        return new Response(sitemapXML(SITE_URL, entries), {
          headers: { "Content-Type": "application/xml", "Cache-Control": "public, max-age=3600" },
        });
      },
    },
  },
});
